import cn from "classnames";
import { FC, useMemo } from "react";

import { useFileProvider, useSettings, useSchool } from "@app/shared/lib/hooks";

import { useTheme } from "@app/shared/providers/ThemeProvider";

import { AccelLogo } from "../AccelLogo";

import styles from "./SchoolLogo.module.scss";

type SchoolLogoProps = {
  theme?: "light" | "dark";
  /** Tone of what the logo sits on, when it is not the page (a coloured header bar); left out = the app theme. */
  background?: "light" | "dark";
  className?: string;
  imageClassname?: string;
  height?: number;
  width?: number;
  /**
   * A school without its own logo shows its name instead of the platform logo (owner, 2026-10-26: no AXL logo
   * in the school's header — «Powered by AXL» is enough).
   */
  nameFallback?: boolean;
};

/**
 * `theme` is the logo's own colour ("dark" = dark artwork for light backgrounds). Left out, it follows the
 * app theme. In the dark theme a school logo uses `darkLogo` when the school uploaded one, otherwise the
 * regular logo as is (owner 2026-10-23: no white plate behind it).
 */
export const SchoolLogo: FC<SchoolLogoProps> = ({
  theme,
  background,
  height = 40,
  width,
  className,
  imageClassname,
  nameFallback,
}) => {
  const school = useSchool();
  const { resolved } = useTheme();
  const dark = (background ?? resolved) === "dark";
  const logoTheme = theme ?? (dark ? "light" : "dark");
  const fileProvider = useFileProvider();
  const appSettings = useSettings();
  const url = useMemo(() => {
    const file = dark && school.darkLogo ? school.darkLogo : school.logo;
    return file ? fileProvider.getUrl(file) : (appSettings.style.logoSrc ?? undefined);
  }, [dark, school.darkLogo, school.logo, fileProvider, appSettings.style.logoSrc]);

  if (nameFallback && !school.logo && !school.darkLogo)
    return (
      <div className={cn("flex", "flex-center", className)}>
        <span className={styles.school_name} style={{ lineHeight: `${height}px` }}>
          {school.name}
        </span>
      </div>
    );

  return (
    <div className={cn("flex", "flex-center", className)}>
      {url ? (
        <img
          alt=""
          src={url}
          className={cn(styles.custom_logo, imageClassname)}
          style={{ height, width }}
        />
      ) : (
        <AccelLogo theme={logoTheme} width={width} height={height} />
      )}
    </div>
  );
};
